import React, { Component } from 'react';
import Style from './index.less';
import classNames from 'classnames';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import {
    OuterShadowPresetStyleList,
    InnerShadowPresetStyleList,
    DefaultEffectPresetProps,
    EffectPropsField,
    PerspectiveShadowPresetStyleList,
} from '@src/framework/editor/src/js/constants';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import i18n from '@i18next/i18n';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { EMPTY_EFFECT } from '../preset-util/base-preset-util';
import { IMAGE_PRESET_ICONS, SHAPE_PRESET_ICONS, TEXT_PRESET_ICONS } from './preset-icons/index';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import { PresetElementType } from '@src/framework/workbench/const/preset-element-type';

const OuterShadowPresetProps = {
    ...DefaultEffectPresetProps[ActionType.SHADOW],
    [EffectPropsField.ANGLE.name]: 0,
    [EffectPropsField.SIZE_X.name]: 0,
    [EffectPropsField.SIZE_Y.name]: 0,
    [EffectPropsField.SKEW_X.name]: 0,
};
const InnerShadowPresetProps = {
    ...DefaultEffectPresetProps[ActionType.SHADOW],
    [EffectPropsField.OPACITY.name]: 50,
    [EffectPropsField.ANGLE.name]: 0,
    [EffectPropsField.SIZE_X.name]: 0,
    [EffectPropsField.SIZE_Y.name]: 0,
    [EffectPropsField.SKEW_X.name]: 0,
    isOuter: false,
};

const ShadowStyleAndPropsMapping = {
    'shadow-empty-click': null,
    'shadow-empty-hover': {
        [EffectPropsField.COLOR.name]: '#000000',
        [EffectPropsField.TD_COLOR.name]: new TDColor({ rgbaStr: '#000000' }),
        [EffectPropsField.OPACITY.name]: 0,
        [EffectPropsField.SIZE.name]: 0,
        [EffectPropsField.BLURRINESS.name]: 0,
        [EffectPropsField.ANGLE.name]: 0,
        [EffectPropsField.DISTANCE.name]: 0,
        isOuter: true,
    },
    'outer-45': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 45, [EffectPropsField.ALGN.name]: EEffectAlgn.TL },
    'outer-90': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 90, [EffectPropsField.ALGN.name]: EEffectAlgn.T },
    'outer-135': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 135, [EffectPropsField.ALGN.name]: EEffectAlgn.TR },
    'outer-0': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 0, [EffectPropsField.ALGN.name]: EEffectAlgn.L },
    'outer-0-phai': { ...OuterShadowPresetProps, [EffectPropsField.SIZE.name]: 102, [EffectPropsField.ALGN.name]: EEffectAlgn.CTR, [EffectPropsField.BLURRINESS.name]: 5, [EffectPropsField.DISTANCE.name]: 0 },
    'outer-180': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 180, [EffectPropsField.ALGN.name]: EEffectAlgn.R },
    'outer-315': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 315, [EffectPropsField.ALGN.name]: EEffectAlgn.BL },
    'outer-270': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 270, [EffectPropsField.ALGN.name]: EEffectAlgn.B },
    'outer-225': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 225, [EffectPropsField.ALGN.name]: EEffectAlgn.BR },

    'inner-225': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 225 },
    'inner-270': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 270 },
    'inner-315': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 315 },
    'inner-180': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 180 },
    'inner-0-phai': { ...InnerShadowPresetProps, [EffectPropsField.OPACITY.name]: 0, [EffectPropsField.BLURRINESS.name]: 9, [EffectPropsField.DISTANCE.name]: 0 },
    'inner-0': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 0 },
    'inner-135': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 135 },
    'inner-90': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 90 },
    'inner-45': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 45 },

    'perspective-1': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.BR,
        [EffectPropsField.ANGLE.name]: 225,
        [EffectPropsField.DISTANCE.name]: 0,
        [EffectPropsField.OPACITY.name]: 80,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: 23,
        [EffectPropsField.SKEW_X.name]: 20,
    },
    'perspective-2': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.BL,
        [EffectPropsField.ANGLE.name]: 315,
        [EffectPropsField.DISTANCE.name]: 0,
        [EffectPropsField.OPACITY.name]: 80,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: 23,
        [EffectPropsField.SKEW_X.name]: -20,
    },
    'perspective-3': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.B,
        [EffectPropsField.ANGLE.name]: 90,
        [EffectPropsField.DISTANCE.name]: 25,
        [EffectPropsField.BLURRINESS.name]: 12,
        [EffectPropsField.OPACITY.name]: 85,
        [EffectPropsField.SIZE_X.name]: 90,
        [EffectPropsField.SIZE_Y.name]: -19,
        [EffectPropsField.SKEW_X.name]: 0,
    },
    'perspective-4': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.BR,
        [EffectPropsField.ANGLE.name]: 135,
        [EffectPropsField.DISTANCE.name]: 1,
        [EffectPropsField.OPACITY.name]: 80,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: -23,
        [EffectPropsField.SKEW_X.name]: 13.34,
    },
    'perspective-5': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.BL,
        [EffectPropsField.ANGLE.name]: 45,
        [EffectPropsField.DISTANCE.name]: 1,
        [EffectPropsField.OPACITY.name]: 80,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: -23,
        [EffectPropsField.SKEW_X.name]: -13.34,
    },
} as const;
type ShadowStyle = typeof ShadowStyleAndPropsMapping[keyof typeof ShadowStyleAndPropsMapping];

interface ShadowPresetMenuProps {
    preset: { style: string };
    onShadowPresetStyleClick: (props: ShadowStyle) => void;
    onShadowPresetStyleMouseEnter: (props: ShadowStyle) => void;
    onShadowPresetStyleMouseLeave: () => void;
    presetElementType?: PresetElementType;
}

export default class ShadowPresetMenu extends Component<ShadowPresetMenuProps> {
    public constructor(props: ShadowPresetMenuProps, context: any) {
        super(props, context);
    }

    public render() {
        const icons = this.getPresetIcons();
        return (
            <div className={classNames(Style.container, 'shadow-preset')}>
                <div className={Style['shadow-type-title']}>{i18n.t('无阴影')}</div>
                <div className={Style['shadow-collection']}>
                    <div className={classNames({
                        [Style['shadow-collection-item']]: true,
                        [Style['shadow-item-selected']]: EMPTY_EFFECT === this.props.preset.style,
                    })}
                    onClick={() => this.handleShadowPresetIconClick('shadow-empty-click')}
                    onMouseEnter={() => this.handleShadowPresetIconMouseEnter('shadow-empty-hover')}
                    onMouseLeave={() => this.handleShadowPresetIconMouseLeave()}
                    >
                        <Tooltip placement={['top', 'right']} title={i18n.t('无')}>
                            <div className={Style['preset-icon']}>
                                <img src={icons[EMPTY_EFFECT]}/>
                            </div>
                        </Tooltip>
                    </div>
                </div>
                <div className={Style['shadow-type-title']}>{i18n.t('外阴影')}</div>
                <div className={Style['shadow-collection']}>
                    {
                        OuterShadowPresetStyleList.map(item => this.getPresetIcon(item))
                    }
                </div>
                <div className={Style['shadow-type-title']}>{i18n.t('内阴影')}</div>
                <div className={Style['shadow-collection']}>
                    {
                        InnerShadowPresetStyleList.map(item => this.getPresetIcon(item))
                    }
                </div>
                <div className={Style['shadow-type-title']}>{i18n.t('透视')}</div>
                <div className={Style['shadow-collection']}>
                    {
                        PerspectiveShadowPresetStyleList.map(item => this.getPresetIcon(item))
                    }
                </div>
            </div>
        );
    }

    private getPresetIcons = () => {
        if (this.props.presetElementType === PresetElementType.IMAGE) {
            return IMAGE_PRESET_ICONS;
        }
        if (this.props.presetElementType === PresetElementType.TEXT) {
            return TEXT_PRESET_ICONS;
        }
        return SHAPE_PRESET_ICONS;
    };

    private getPresetIcon = (item) => {
        const shadowItemClass = classNames({
            [Style['shadow-collection-item']]: true,
            [Style['shadow-item-selected']]: item.style === this.props.preset.style,
        });
        const icons = this.getPresetIcons();
        return (
            <div key={item.key}
                className={shadowItemClass}
                onMouseEnter={() => {
                    this.handleShadowPresetIconMouseEnter(item.style);
                }}
                onClick={() => {
                    this.handleShadowPresetIconClick(item.style);
                }}
                onMouseLeave={() => {
                    this.handleShadowPresetIconMouseLeave();
                }}>
                <Tooltip placement={['top', 'right']} title={i18n.t(item.title)}>
                    <div className={Style['preset-icon']}>
                        <img src={icons[item.style]}/>
                    </div>
                </Tooltip>
            </div>
        );
    };

    private handleShadowPresetIconMouseEnter = (style: string) => {
        const value = ShadowStyleAndPropsMapping[style];
        this.props.onShadowPresetStyleMouseEnter(value ? { ...value } : value);
    };

    private  handleShadowPresetIconClick = (style: string) => {
        const value = ShadowStyleAndPropsMapping[style];
        this.props.onShadowPresetStyleClick(value ? { ...value } : value);
    };

    private  handleShadowPresetIconMouseLeave = () => {
        this.props.onShadowPresetStyleMouseLeave();
    };
}


